import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { DOMAIN_LEARNING_STATUS, STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * Domain-level Learning History, as the domain's Learning tab reviews it.
 *
 * Mirrors `domain_learning_insights` minus what reviewers must not see: the API returns how many
 * users back a learning (`supporting_user_count`), never which ones.
 */

export type DomainLearningStatus =
  (typeof DOMAIN_LEARNING_STATUS)[keyof typeof DOMAIN_LEARNING_STATUS];

export type DomainLearningReviewAction =
  | "approve"
  | "reject"
  | "accept_revision"
  | "dismiss_revision";

export type DomainLearningEvidenceItem = {
  label: string;
  detail: string;
  occurred_at: string;
};

export type DomainLearningRevision = {
  title: string;
  summary: string;
  detailed_context: string;
};

export type DomainLearning = {
  id: number;
  domain_id: number;
  category: string;
  title: string;
  summary: string;
  detailed_context: string;
  interaction_count: number;
  supporting_user_count: number;
  evidence: DomainLearningEvidenceItem[];
  status: DomainLearningStatus;
  pending_revision: DomainLearningRevision | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  computed_at: string;
  created_at: string;
  updated_at: string;
};

export type DomainLearningReviewDecision = {
  learning_id: number;
  action: DomainLearningReviewAction;
  title?: string;
  summary?: string;
  detailed_context?: string;
};

export type DomainLearningsState = {
  domainId: number | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  learnings: DomainLearning[];
  isReviewing: boolean;
  reviewError: string | null;
};

export const domainLearningsInitialState: DomainLearningsState = {
  domainId: null,
  status: STATUS.IDLE,
  error: null,
  learnings: [],
  isReviewing: false,
  reviewError: null,
};

const STATUSES: ReadonlySet<string> = new Set(Object.values(DOMAIN_LEARNING_STATUS));

function isEvidenceItem(value: unknown): value is DomainLearningEvidenceItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.label === "string" &&
    typeof row.detail === "string" &&
    typeof row.occurred_at === "string"
  );
}

function isRevision(value: unknown): value is DomainLearningRevision {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.title === "string" &&
    typeof row.summary === "string" &&
    typeof row.detailed_context === "string"
  );
}

function isDomainLearning(value: unknown): value is DomainLearning {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.domain_id === "number" &&
    typeof row.category === "string" &&
    typeof row.title === "string" &&
    typeof row.summary === "string" &&
    typeof row.detailed_context === "string" &&
    typeof row.interaction_count === "number" &&
    typeof row.supporting_user_count === "number" &&
    Array.isArray(row.evidence) &&
    row.evidence.every(isEvidenceItem) &&
    typeof row.status === "string" &&
    STATUSES.has(row.status) &&
    (row.pending_revision === null || isRevision(row.pending_revision)) &&
    (typeof row.reviewed_by === "string" || row.reviewed_by === null) &&
    (typeof row.reviewed_at === "string" || row.reviewed_at === null) &&
    typeof row.computed_at === "string"
  );
}

async function readLearnings(res: Response): Promise<DomainLearning[] | null> {
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isDomainLearning)) {
    return null;
  }
  return data;
}

export const fetchDomainLearnings = createAsyncThunk<
  DomainLearning[],
  number,
  { rejectValue: string }
>("domainLearnings/fetch", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/learnings`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const learnings = await readLearnings(res);
    return learnings ?? rejectWithValue("Unexpected response shape");
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

/** One reviewer sitting: every decision lands, or none do. Returns the domain's whole list. */
export const reviewDomainLearnings = createAsyncThunk<
  DomainLearning[],
  { domainId: number; decisions: DomainLearningReviewDecision[] },
  { rejectValue: string }
>("domainLearnings/review", async ({ domainId, decisions }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/learnings/review-batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decisions }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const learnings = await readLearnings(res);
    return learnings ?? rejectWithValue("Unexpected response shape");
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

const domainLearningsSlice = createSlice({
  name: "domainLearnings",
  initialState: domainLearningsInitialState,
  reducers: {
    clearDomainLearningReviewError(state) {
      state.reviewError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDomainLearnings.pending, (state, action) => {
        // A different domain must never show the previous one's list while it loads.
        if (state.domainId !== action.meta.arg) {
          state.learnings = [];
        }
        state.domainId = action.meta.arg;
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchDomainLearnings.fulfilled, (state, action) => {
        if (state.domainId !== action.meta.arg) {
          return;
        }
        state.status = STATUS.SUCCEEDED;
        state.learnings = action.payload;
      })
      .addCase(fetchDomainLearnings.rejected, (state, action) => {
        if (state.domainId !== action.meta.arg) {
          return;
        }
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(reviewDomainLearnings.pending, (state) => {
        state.isReviewing = true;
        state.reviewError = null;
      })
      .addCase(reviewDomainLearnings.fulfilled, (state, action) => {
        state.isReviewing = false;
        if (state.domainId === action.meta.arg.domainId) {
          state.learnings = action.payload;
        }
      })
      .addCase(reviewDomainLearnings.rejected, (state, action) => {
        state.isReviewing = false;
        state.reviewError = action.payload ?? "Request failed";
      });
  },
});

export const { clearDomainLearningReviewError } = domainLearningsSlice.actions;
export const domainLearningsReducer = domainLearningsSlice.reducer;

export const selectDomainLearnings = (state: RootState) => state.domainLearnings.learnings;
export const selectDomainLearningsStatus = (state: RootState) => state.domainLearnings.status;
export const selectDomainLearningsError = (state: RootState) => state.domainLearnings.error;
export const selectDomainLearningsIsReviewing = (state: RootState) =>
  state.domainLearnings.isReviewing;
export const selectDomainLearningsReviewError = (state: RootState) =>
  state.domainLearnings.reviewError;
